import React, { FC, useEffect, useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Placeholder, withSitecoreContext, ComponentRendering } from '@sitecore-jss/sitecore-jss-react';
import { CtaStylesCtaTypeObj, TGenericPopUpProps } from './types/genericPopUp.types';
import { documentService } from '../../../utils/window.utils.js';
import Icon from '../icon';
import { PropsFromGenericPopup } from '../../../types/types';
import CTA from '../CTA';
import ModalDialog from '../modalDialog';
import { GenericPopUpContext } from '../genericPopUpContextProvider';
import './styles.scss';

const SharedGenericPopUp: FC<TGenericPopUpProps> = (props) => {
  // Paraams isn't loaded on load. So hardcoded for now but will remove later
  const { children, extraClassName = '' } = props;
  const ctaText = props?.params?.ctaText || 'Generic Pop Up';
  const isEEActive = props?.sitecoreContext?.pageEditing;
  const iconField = props?.fields?.icon;
  const [isPopUpShown, setIsPopUpShown] = useState(false);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const ctaOpenTriggerRef = useRef<HTMLDivElement | null>(null);
  const ctaCloseTriggerRef = useRef<HTMLDivElement | null>(null);
  const popUpClassName = 'generic-modal-dialog';
  const modalDialogConatinerClass = 'my-modal-dialog__overlay';
  const modalDialogContentlass = 'my-modal-dialog';
  const modalDialogCloseBtnClass = 'my-modal-dialog__close-button';
  const placeholderRef = useRef<HTMLDivElement | null>(null);
  const editorContentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isEEActive) {
      const startStopBodyScroll = (isScrollable: boolean): void => {
        const docElement = documentService();
        if (!docElement?.body) return;
        return isScrollable ? docElement.body.classList.remove('body--with-pop-up') : docElement.body.classList.add('body--with-pop-up');
      };

      const openCTA = (event): void => {
        if (event.ctrlKey || event.metaKey) {
          event?.stopPropagation();
          event?.preventDefault();
          editorRef.current?.classList.add('editor_popup--visible');
          startStopBodyScroll(false);
        }
      };

      const closeCTA = (event): void => {
        if (event.ctrlKey || event.metaKey) {
          event?.stopPropagation();
          event?.preventDefault();
          editorRef.current?.classList.remove('editor_popup--visible');
          startStopBodyScroll(true);
        }
      };

      ctaOpenTriggerRef.current?.addEventListener('click', openCTA);
      ctaCloseTriggerRef.current?.addEventListener('click', closeCTA);

      return (): void => {
        ctaOpenTriggerRef.current?.removeEventListener('click', openCTA); // eslint-disable-line
        ctaCloseTriggerRef.current?.removeEventListener('click', closeCTA); // eslint-disable-line
      };
    }
  }, []); // eslint-disable-line

  const EditorPopUp = useMemo(() => {
    return (
      <div
        className={`${modalDialogConatinerClass} ${popUpClassName} editor_popup`}
        ref={editorRef}
        style={{ display: isEEActive ? 'block' : 'none' }}
      >
        <div className={`${modalDialogCloseBtnClass} editor_popup__close`} ref={ctaCloseTriggerRef}>
          <Icon iconName="action-close" size={40} extraClassName="editor_popup__close-icon" />
        </div>
        <div className={`${modalDialogContentlass} ${popUpClassName} editor_popup__content`} ref={editorContentRef}></div>
      </div>
    );
  }, []); // eslint-disable-line

  useEffect(() => {
    if (isEEActive) {
      if (editorContentRef.current && placeholderRef.current) {
        editorContentRef.current?.appendChild?.(placeholderRef?.current?.children?.[0]);
      }
    }
    placeholderRef.current?.remove?.();
  }, [editorContentRef, placeholderRef]); // eslint-disable-line

  const propsToInnerComp: PropsFromGenericPopup = {
    isInGenericPopup: true,
  };

  return (
    <div className={`generic-popup ${extraClassName}`}>
      <div
        className="generic-popup__button"
        ref={ctaOpenTriggerRef}
        onClick={(): void => {
          !isEEActive && setIsPopUpShown(true);
        }}
      >
        {
          children ? children :
            <CTA iconField={iconField} type={CtaStylesCtaTypeObj[props.params?.ctaStyles || 'Primary Orange']}>
              {ctaText}
            </CTA>
        }
      </div>
      <div ref={placeholderRef} className="editor_popup__placeholder-ref">
        <div>
          <Placeholder name="jss-popup" rendering={props.rendering as ComponentRendering} {...propsToInnerComp} />
        </div>
      </div>

      {documentService()?.getElementById?.('root') &&
        createPortal(EditorPopUp, documentService()?.getElementById?.('root') as HTMLDivElement)}

      {props.rendering && (
        <ModalDialog stopPropagationOnClose={true} isOpen={!isEEActive && isPopUpShown} className={popUpClassName} close={(): void => setIsPopUpShown(false)}>
          <GenericPopUpContext.Provider value={{ isGenericPopUp: true }}>
            <Placeholder name={'jss-popup'} rendering={props.rendering} {...propsToInnerComp} />
          </GenericPopUpContext.Provider>
        </ModalDialog>
      )}
    </div>
  );
};

export default withSitecoreContext()(SharedGenericPopUp);
